import { handleRequest } from "../../axios";
import { axiosInstance } from "./JsdhNotifikaceService";
import {
    PUSH_VAPID_PUBLIC_KEY_URL,
    PUSH_SUBSCRIBE_URL,
    PUSH_UNSUBSCRIBE_URL,
} from "../url";
import type { VapidKeyResponse } from "../generated/models/VapidKeyResponse";
import type { PushSubscriptionDto } from "../generated/models/PushSubscriptionDto";

// -----------------------------
// VAPID KEY
// -----------------------------

const getVapidPublicKey = () => {
    return handleRequest<VapidKeyResponse>(() =>
        axiosInstance.get(PUSH_VAPID_PUBLIC_KEY_URL)
    );
};

// -----------------------------
// SUBSCRIBE
// -----------------------------

const subscribe = (subscription: PushSubscriptionDto) => {
    return handleRequest(() =>
        axiosInstance.post(PUSH_SUBSCRIBE_URL, subscription)
    );
};

// -----------------------------
// UNSUBSCRIBE
// -----------------------------

const unsubscribe = (endpoint: string) => {
    return handleRequest(() =>
        axiosInstance.delete(PUSH_UNSUBSCRIBE_URL, {
            params: { endpoint },
        })
    );
};

// -----------------------------
// HELPERS
// -----------------------------

/**
 * Converts a base64 string to Uint8Array (for VAPID key)
 */
export const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
};

/**
 * Converts ArrayBuffer to base64 string
 */
export const arrayBufferToBase64 = (buffer: ArrayBuffer | null): string => {
    if (!buffer) return '';
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
};

/**
 * Checks if push notifications are supported in this browser
 */
export const isPushSupported = (): boolean => {
    return 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Gets current push notification permission status
 */
export const getPushPermissionStatus = (): NotificationPermission | 'unsupported' => {
    if (!isPushSupported()) return 'unsupported';
    return Notification.permission;
};

/**
 * Requests push notification permission
 */
export const requestPushPermission = async (): Promise<NotificationPermission> => {
    return await Notification.requestPermission();
};

// -----------------------------

const PushService = {
    getVapidPublicKey,
    subscribe,
    unsubscribe,
    // Helpers
    urlBase64ToUint8Array,
    arrayBufferToBase64,
    isPushSupported,
    getPushPermissionStatus,
    requestPushPermission,
};

export default PushService;
